import { match } from 'ts-pattern';
import { z } from 'zod';
import type { Localization } from './Localization';
import type { components } from '~~/types/Api';
import { exhaustive } from '~~/types/Exhaustive';

export type PayoutModalities = components['schemas']['PayoutModalities'];

export type ProgrammePayoutType = components['schemas']['ProgrammePayoutType'];

export const PROGRAMME_PAYOUT_TYPES = exhaustive<ProgrammePayoutType>()(['Unknown', 'Cpa', 'CpaCps', 'Cpc', 'Cpm', 'Cps']);

export const payoutTypeEnum = z.enum(PROGRAMME_PAYOUT_TYPES);

export const payoutModalitiesSchema: z.ZodType<PayoutModalities> = z.object({
	payoutType: payoutTypeEnum,
	fixedInEur: z.number().nullable(),
	proportionalInPct: z.number(),
	referrerBonusInPct: z.number(),
	publisherFixedInEur: z.number().nullable().optional(),
	publisherProportionalInPct: z.number().nullable().optional(),
	publisherBonusInPct: z.number().nullable().optional(),
});

export const createPayoutModalityFromSingleValue = (payoutType: ProgrammePayoutType, value: number | null | undefined): PayoutModalities => {
	if (!value) {
		return { payoutType, fixedInEur: 0, proportionalInPct: 0, referrerBonusInPct: 0 };
	}

	return match(payoutType)
		.returnType<PayoutModalities>()
		.with('Cpa', (p) => ({ payoutType: p, fixedInEur: value, proportionalInPct: 0, referrerBonusInPct: 0 }))
		.with('CpaCps', (p) => ({ payoutType: p, fixedInEur: value, proportionalInPct: 0, referrerBonusInPct: 0 }))
		.with('Cpc', (p) => ({ payoutType: p, fixedInEur: value, proportionalInPct: 0, referrerBonusInPct: 0 }))
		.with('Cpm', (p) => ({ payoutType: p, fixedInEur: value, proportionalInPct: 0, referrerBonusInPct: 0 }))
		.with('Cps', (p) => ({ payoutType: p, fixedInEur: 0, proportionalInPct: value, referrerBonusInPct: 0 }))
		.with('Unknown', (p) => ({ payoutType: p, fixedInEur: 0, proportionalInPct: 0, referrerBonusInPct: 0 }))
		.exhaustive();
};

export const getProgrammePayoutModalityValue = (payoutModality: PayoutModalities, forceOriginalValues = false): string => {
	const percentFormatter = new Intl.NumberFormat('de-DE', { style: 'percent', minimumFractionDigits: 2, unitDisplay: 'narrow' });
	const currencyFormatter = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2, unitDisplay: 'narrow' });

	const fixedInEur = (forceOriginalValues ? payoutModality.fixedInEur : (payoutModality.publisherFixedInEur ?? payoutModality.fixedInEur)) ?? 0;
	const proportionalInPct = (forceOriginalValues ? payoutModality.proportionalInPct : (payoutModality.publisherProportionalInPct ?? payoutModality.proportionalInPct)) ?? 0;

	return match(payoutModality)
		.returnType<string>()
		.with({ payoutType: 'Cpa' }, () => `${currencyFormatter.format(fixedInEur)} per Action`)
		.with({ payoutType: 'CpaCps' }, () => `${currencyFormatter.format(fixedInEur)} per Conversion + ${percentFormatter.format(proportionalInPct / 100)} of Sale`)
		.with({ payoutType: 'Cpc' }, () => `${currencyFormatter.format(fixedInEur)} per Click`)
		.with({ payoutType: 'Cpm' }, () => `${currencyFormatter.format(fixedInEur)} per Thousand Impressions`)
		.with({ payoutType: 'Cps' }, () => `${percentFormatter.format(proportionalInPct / 100)} per Sale`)
		.with({ payoutType: 'Unknown' }, () => `Unbekannt`)
		.exhaustive();
};

export const hasPublisherSpecificPayout = (payoutModality: PayoutModalities): boolean => match(payoutModality)
	.returnType<boolean>()
	.with({ payoutType: 'Cpa' }, (p) => p.publisherFixedInEur != null)
	.with({ payoutType: 'CpaCps' }, (p) => p.publisherFixedInEur != null || p.publisherProportionalInPct != null)
	.with({ payoutType: 'Cpc' }, (p) => p.publisherFixedInEur != null)
	.with({ payoutType: 'Cpm' }, (p) => p.publisherFixedInEur != null)
	.with({ payoutType: 'Cps' }, (p) => p.publisherProportionalInPct != null)
	.with({ payoutType: 'Unknown' }, () => false)
	.exhaustive();

export const getProgrammePayoutModalityDisplayValue = (payoutModality: PayoutModalities): Localization => {
	const value = getProgrammePayoutModalityValue(payoutModality);

	if (hasPublisherSpecificPayout(payoutModality)) {
		return () => value;
	}

	return (t) => t('label.payoutFrom', { value });
};

export const getReferrerBonusDisplayValue = (payoutModality: PayoutModalities) => {
	if (!payoutModality.publisherBonusInPct) {
		return null;
	}

	const percentFormatter = new Intl.NumberFormat('de-DE', { style: 'percent', minimumFractionDigits: 2, unitDisplay: 'narrow' });

	return `${percentFormatter.format(payoutModality.publisherBonusInPct / 100)} per Sale`;
};
